import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { UI } from '../constants';

const CHROME_EXTENSION_URL =
  'https://chrome.google.com/webstore/detail/metamask/nkbihfbeogaeaoehlefnkodbefgpgknn';

const APP_STORE_URL = 'https://apps.apple.com/app/metamask/id1438144202';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=io.metamask';

const METAMASK_DOWNLOAD_URL = 'https://metamask.io/download/';

const STEPS = [
  {
    n: '01',
    title: 'Pick a direction and an amount',
    body: 'Choose what you are sending and how it should land. The payout figure updates as you type.',
  },
  {
    n: '02',
    title: 'Lock the rate',
    body: 'The quote holds for 60 seconds. Nothing moves until you approve that exact number.',
  },
  {
    n: '03',
    title: 'Approve or pay',
    body: 'Send to Somalia: approve the deposit in your wallet. Fund your wallet: pay by QR from your mobile money.',
    metamask: true,
  },
  {
    n: '04',
    title: 'We verify, then payout',
    body: 'Once the payment is confirmed, the payout is released — cash to the recipient, or crypto to your wallet.',
  },
];

function getMetaMaskDownloadInfo() {
  if (typeof navigator === 'undefined') {
    return {
      url: METAMASK_DOWNLOAD_URL,
      label: 'Get MetaMask',
    };
  }

  const userAgent = navigator.userAgent || '';

  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return {
      url: APP_STORE_URL,
      label: 'Get MetaMask for iPhone',
    };
  }

  if (/Android/i.test(userAgent)) {
    return {
      url: PLAY_STORE_URL,
      label: 'Get MetaMask for Android',
    };
  }

  return {
    url: CHROME_EXTENSION_URL,
    label: 'Install MetaMask for Chrome',
  };
}

function MetaMaskLogo({ size = 22 }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 212 189"
      sx={{ width: size, height: size, flexShrink: 0 }}
      aria-hidden="true"
    >
      <polygon
        fill="#E17726"
        stroke="#E17726"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="201.6,0 118.7,61.4 134.1,25.1"
      />
      <polygon
        fill="#E27625"
        stroke="#E27625"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="10.1,0 92.3,62 77.7,25.1"
      />
      <polygon
        fill="#E27625"
        stroke="#E27625"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="172.2,136.9 150.1,170.7 197.4,183.7 210.9,137.6"
      />
      <polygon
        fill="#E27625"
        stroke="#E27625"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="0.8,137.6 14.3,183.7 61.6,170.7 39.5,136.9"
      />
      <polygon
        fill="#E27625"
        stroke="#E27625"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="59,82.1 45.9,101.9 92.8,104 91.2,53.6"
      />
      <polygon
        fill="#E27625"
        stroke="#E27625"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="152.7,82.1 119.9,53 118.7,104 165.6,101.9"
      />
      <polygon
        fill="#E27625"
        stroke="#E27625"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="61.6,170.7 89.9,157 65.5,137.9"
      />
      <polygon
        fill="#E27625"
        stroke="#E27625"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="121.8,157 150.1,170.7 146.2,137.9"
      />
      <polygon
        fill="#D5BFB2"
        stroke="#D5BFB2"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="150.1,170.7 121.8,157 124.1,175.4 123.8,183.2"
      />
      <polygon
        fill="#D5BFB2"
        stroke="#D5BFB2"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="61.6,170.7 87.9,183.2 87.7,175.4 89.9,157"
      />
      <polygon
        fill="#233447"
        stroke="#233447"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="88.4,125.5 64.8,118.6 81.4,111"
      />
      <polygon
        fill="#233447"
        stroke="#233447"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="123.2,125.5 130.2,111 146.9,118.6"
      />
      <polygon
        fill="#CC6228"
        stroke="#CC6228"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="61.6,170.7 65.7,136.9 39.5,137.6"
      />
      <polygon
        fill="#CC6228"
        stroke="#CC6228"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="146,136.9 150.1,170.7 172.2,137.6"
      />
      <polygon
        fill="#CC6228"
        stroke="#CC6228"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="165.6,101.9 118.7,104 123.2,125.5 130.2,111 146.9,118.6"
      />
      <polygon
        fill="#CC6228"
        stroke="#CC6228"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="64.8,118.6 81.4,111 88.4,125.5 92.8,104 45.9,101.9"
      />
      <polygon
        fill="#E27525"
        stroke="#E27525"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="45.9,101.9 65.5,137.9 64.8,118.6"
      />
      <polygon
        fill="#E27525"
        stroke="#E27525"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="146.9,118.6 146.2,137.9 165.6,101.9"
      />
      <polygon
        fill="#E27525"
        stroke="#E27525"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="92.8,104 88.4,125.5 93.9,154.3 95.2,116.3"
      />
      <polygon
        fill="#E27525"
        stroke="#E27525"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="118.7,104 116.4,116.2 117.6,154.3 123.2,125.5"
      />
      <polygon
        fill="#F5841F"
        stroke="#F5841F"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="123.2,125.5 117.6,154.3 121.8,157 146.2,137.9 146.9,118.6"
      />
      <polygon
        fill="#F5841F"
        stroke="#F5841F"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="64.8,118.6 65.5,137.9 89.9,157 93.9,154.3 88.4,125.5"
      />
      <polygon
        fill="#C0AC9D"
        stroke="#C0AC9D"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="123.8,183.2 124.1,175.4 122,173.5 89.8,173.5 87.7,175.4 87.9,183.2 61.6,170.7 70.8,178.3 89.4,191.2 122.4,191.2 141.1,178.3 150.1,170.7"
      />
      <polygon
        fill="#161616"
        stroke="#161616"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="121.8,157 117.6,154.3 94.2,154.3 89.9,157 87.7,175.4 89.8,173.5 122,173.5 124.1,175.4"
      />
      <polygon
        fill="#763E1A"
        stroke="#763E1A"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="205.1,65.4 212,32.3 201.6,0 121.8,59.2 152.7,82.1 196.3,94.9 205.9,83.7 201.7,80.7 208.3,74.7 203.2,70.8 209.8,65.7"
      />
      <polygon
        fill="#763E1A"
        stroke="#763E1A"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="0,32.3 6.9,65.4 2.5,68.7 9.1,73.7 4.1,77.7 10.7,83.7 6.5,86.7 16.1,97.9 59.7,85.1 90.6,62.2 10.8,3 0,35.3"
      />
      <polygon
        fill="#F5841F"
        stroke="#F5841F"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="196.3,94.9 152.7,82.1 165.6,101.9 146.2,137.9 172.2,137.6 210.9,137.6"
      />
      <polygon
        fill="#F5841F"
        stroke="#F5841F"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="59,82.1 16.1,97.9 0.8,137.6 39.5,137.6 65.5,137.9 45.9,101.9"
      />
      <polygon
        fill="#F5841F"
        stroke="#F5841F"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="118.7,104 121.8,59.2 134.2,25.1 77.7,25.1 91.2,59.2 95.2,104 96.7,116.3 96.6,154.3 117.6,154.3 117.7,116.3"
      />
    </Box>
  );
}

MetaMaskLogo.propTypes = {
  size: PropTypes.number,
};

function renderMetaMaskBlock(metaMask, onClick) {
  return (
    <Box sx={{ mt: 2.5 }}>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 600,
          color: UI.textFaint,
          mb: 1.25,
        }}
      >
        Don&apos;t have MetaMask?
      </Typography>

      <Button
        variant="contained"
        onClick={onClick}
        startIcon={<MetaMaskLogo size={22} />}
        sx={{
          height: 52,
          px: 3,
          borderRadius: 3,
          fontSize: 15,
          fontWeight: 800,
          letterSpacing: '-0.01em',
          textTransform: 'none',
          color: '#F6851B',
          bgcolor: '#1A1205',
          border: '1px solid rgba(246,133,27,0.4)',
          boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
          '& .MuiButton-startIcon': { mr: 1.25 },
          '&:hover': {
            bgcolor: '#241a0a',
            borderColor: 'rgba(246,133,27,0.6)',
            boxShadow: '0 8px 26px rgba(0,0,0,0.45)',
          },
        }}
      >
        {metaMask.label}
      </Button>
    </Box>
  );
}

export default function StepsSection() {
  const metaMask = getMetaMaskDownloadInfo();

  const handleGetMetaMask = () => {
    window.open(metaMask.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box
      id="how"
      sx={{
        bgcolor: UI.bg,
        borderTop: `1px solid ${UI.border}`,
        borderBottom: `1px solid ${UI.border}`,
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Typography
          sx={{
            fontFamily: UI.mono,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: UI.green,
            mb: 2,
          }}
        >
          How it works
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: '2.4rem', md: '3.4rem' },
            fontWeight: 900,
            lineHeight: 1.02,
            letterSpacing: '-0.035em',
            color: UI.text,
          }}
        >
          Four steps, no
          <br />
          account gymnastics
        </Typography>

        <Typography
          sx={{
            mt: 3,
            fontSize: { xs: '1.05rem', md: '1.2rem' },
            lineHeight: 1.55,
            color: UI.textMuted,
            maxWidth: 620,
          }}
        >
          You see the exact payout amount before anything moves. If the rate slips past your lock,
          we requote — nothing gets silently repriced.
        </Typography>

        <Box
          sx={{
            mt: { xs: 6, md: 8 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: { xs: 2.5, md: 3 },
          }}
        >
          {STEPS.map((step) => (
            <Box
              key={step.n}
              sx={{
                position: 'relative',
                p: { xs: 3, md: 4 },
                borderRadius: 4,
                bgcolor: UI.card,
                border: `1px solid ${UI.border}`,
                overflow: 'hidden',
                transition: 'border-color 160ms ease, transform 160ms ease',
                '&:hover': {
                  borderColor: UI.green,
                  transform: 'translateY(-3px)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: 4,
                  height: '100%',
                  background: UI.gradient,
                  opacity: 0.9,
                },
              }}
            >
              <Stack direction="row" spacing={2.5} alignItems="flex-start">
                <Typography
                  sx={{
                    fontFamily: UI.mono,
                    fontSize: { xs: 34, md: 42 },
                    fontWeight: 700,
                    lineHeight: 1,
                    color: UI.green,
                    opacity: 0.9,
                  }}
                >
                  {step.n}
                </Typography>

                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: 20, md: 22 },
                      fontWeight: 800,
                      letterSpacing: '-0.02em',
                      lineHeight: 1.15,
                      color: UI.text,
                    }}
                  >
                    {step.title}
                  </Typography>

                  <Typography
                    sx={{
                      mt: 1.25,
                      fontSize: { xs: 15, md: 16 },
                      color: UI.textMuted,
                      lineHeight: 1.65,
                    }}
                  >
                    {step.body}
                  </Typography>

                  {step.metamask ? renderMetaMaskBlock(metaMask, handleGetMetaMask) : null}
                </Box>
              </Stack>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
}
